import { useRef } from "react";
import {
  ModalManager,
  useModal,
} from "@circle-react/components/Layout/ModalManagerProvider";
import { Form } from "@circle-react-uikit/Form";
import { Modal } from "@circle-react-uikit/ModalV2";
import { AddLinkFormBody } from "./AddLinkForm";
import { AddLinkHeader } from "./AddLinkHeader";
import { RemoveLinkButton } from "./RemoveLinkButton";
import { VALIDATION_SCHEMA } from "./validationSchema";

export interface AddTipTapLinkModalProps {
  editor: any;
  isAdminUsageContext?: boolean;
  isEmailEditor?: boolean;
}

export const AddTipTapLinkModalComponent = ({
  editor,
  isAdminUsageContext = false,
  isEmailEditor = false,
}: AddTipTapLinkModalProps) => {
  const drawer = useModal();
  const isEditable = editor.isActive("link");
  const inputRef = useRef(null);

  const previousURL = editor.getAttributes("link")?.href;

  const handleRemoveLink = () => {
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
    void drawer.hide();
  };

  const handleOnSubmit = async (
    { url }: { url: string },
    { setError }: any,
  ) => {
    try {
      await VALIDATION_SCHEMA.validate({ url });

      const isEditingLink = editor.isActive("link");
      const { from, to } = editor.state.selection;
      const hasSelection = from !== to;
      const selectedText = editor.state.doc.textBetween(from, to);
      const isOnlyWhitespace = hasSelection && selectedText.trim() === "";

      if (
        isEditingLink ||
        (hasSelection && !isOnlyWhitespace) ||
        !isEmailEditor
      ) {
        // Edit existing link or add link to selected text
        editor
          .chain()
          .focus()
          .extendMarkRange("link")
          .setLink({ href: url })
          .run();
      } else {
        // Email Editors: insert new link at cursor when no selection or when an empty space is selected
        editor
          .chain()
          .focus()
          .insertContent([
            {
              type: "text",
              text: url,
              marks: [{ type: "link", attrs: { href: url, target: "_blank" } }],
            },
          ])
          .run();
      }

      void drawer.hide();
    } catch (error: any) {
      setError("url", {
        type: "manual",
        message: error.message || "Invalid URL",
      });
    }
  };

  const handleClose = () => {
    void drawer.hide();
  };

  return (
    <Modal
      isOpen={drawer.visible}
      onClose={drawer.hide}
      initialFocus={inputRef}
    >
      <Modal.Overlay />
      <Modal.Content>
        <AddLinkHeader isEditable={isEditable} onClose={handleClose} />
        <Form
          onSubmit={handleOnSubmit}
          defaultValues={{ url: previousURL }}
          autoComplete="off"
        >
          <AddLinkFormBody
            inputRef={inputRef}
            submitButtonVariant={isAdminUsageContext ? "circle" : "primary"}
          >
            <RemoveLinkButton
              isEditable={isEditable}
              onClick={handleRemoveLink}
            />
          </AddLinkFormBody>
        </Form>
      </Modal.Content>
    </Modal>
  );
};

const AddTipTapLinkModal = ModalManager.create(
  ({ editor, isAdminUsageContext, isEmailEditor }: AddTipTapLinkModalProps) => (
    <AddTipTapLinkModalComponent
      editor={editor}
      isAdminUsageContext={isAdminUsageContext}
      isEmailEditor={isEmailEditor}
    />
  ),
);

export const useAddTipTapLinkModal = () => useModal(AddTipTapLinkModal);
